import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, MapPin, FileText, AlertCircle } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, getEquipmentStatus, getStatusColor, getStatusLabel, getDaysUntil } from '../../utils/helpers';
import { EQUIPMENT_ICONS, EQUIPMENT_TYPE_LABELS } from '../../utils/constants';

const EquipmentDetails = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { id } = useParams();
  const [equipment, setEquipment] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEquipment();
  }, [id]);

  const loadEquipment = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEquipmentById(id);
      if (user?.role === 'client' && data && Number(data.clientId) !== Number(user.id)) {
        setEquipment(null);
        return;
      }
      setEquipment(data);
    } catch (error) {
      console.error('Failed to load equipment:', error);
      console.error('Equipment ID:', id);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Équipement non trouvé</p>
      </div>
    );
  }

  const status = getEquipmentStatus(equipment);
  const daysUntilMaintenance = equipment.nextMaintenanceDate ? getDaysUntil(equipment.nextMaintenanceDate) : null;
  const daysUntilExpiration = equipment.expirationDate ? getDaysUntil(equipment.expirationDate) : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="outline"
          icon={ArrowLeft}
          onClick={() => navigate('/my-sites')}
          className="mb-4"
        >
          Retour
        </Button>
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <div className="text-5xl">{EQUIPMENT_ICONS[equipment.type]}</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{EQUIPMENT_TYPE_LABELS[equipment.type] || equipment.type}</h1>
              <p className="text-gray-600 mt-1">{equipment.serialNumber}</p>
            </div>
          </div>
          <span className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
            {getStatusLabel(status)}
          </span>
        </div>
      </div>

      {/* Alerts */}
      {(daysUntilMaintenance !== null && daysUntilMaintenance <= 30) || (daysUntilExpiration !== null && daysUntilExpiration <= 30) ? (
        <Card>
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">Attention requise</h3>
              <ul className="space-y-1 text-sm text-gray-600">
                {daysUntilMaintenance !== null && daysUntilMaintenance <= 30 && (
                  <li>
                    • Maintenance prévue dans {daysUntilMaintenance} jour(s)
                  </li>
                )}
                {daysUntilExpiration !== null && daysUntilExpiration <= 30 && (
                  <li>
                    • Expiration dans {daysUntilExpiration} jour(s)
                  </li>
                )}
              </ul>
            </div>
          </div>
        </Card>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Informations générales</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Type</label>
                <p className="text-gray-900 font-medium">{EQUIPMENT_TYPE_LABELS[equipment.type] || equipment.type}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600">Numéro de série</label>
                <p className="text-gray-900 font-medium">{equipment.serialNumber}</p>
              </div>
              {equipment.extinguisherType && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Type d'extincteur</label>
                  <p className="text-gray-900 font-medium">{equipment.extinguisherType}</p>
                </div>
              )}
              {equipment.capacity && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Capacité</label>
                  <p className="text-gray-900 font-medium">{equipment.capacity}</p>
                </div>
              )}
              {equipment.vehicleRegistration && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Matricule du véhicule</label>
                  <p className="text-gray-900 font-medium">{equipment.vehicleRegistration}</p>
                </div>
              )}
              {(equipment.siteName || equipment.site) && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Site</label>
                  <p className="text-gray-900 font-medium">{equipment.siteName || equipment.site}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Emplacement et État Physique Table */}
          {(equipment.location || equipment.physicalCondition || equipment.spareParts || equipment.firstAidKit || equipment.notes) && (
            <Card>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Emplacement et État Physique</h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 border border-gray-200 w-1/4">
                        Champ
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 border border-gray-200">
                        Valeur
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipment.location && (
                      <tr>
                        <td className="px-4 py-3 border border-gray-200 bg-gray-50 font-medium text-gray-700">
                          Emplacement
                        </td>
                        <td className="px-4 py-3 border border-gray-200 text-gray-900">
                          {equipment.location}
                        </td>
                      </tr>
                    )}
                    {equipment.physicalCondition && (
                      <tr>
                        <td className="px-4 py-3 border border-gray-200 bg-gray-50 font-medium text-gray-700">
                          Aspect Physique
                        </td>
                        <td className="px-4 py-3 border border-gray-200 text-gray-900 whitespace-pre-wrap">
                          {equipment.physicalCondition}
                        </td>
                      </tr>
                    )}
                    {equipment.spareParts && (
                      <tr>
                        <td className="px-4 py-3 border border-gray-200 bg-gray-50 font-medium text-gray-700">
                          Pièces détachées
                        </td>
                        <td className="px-4 py-3 border border-gray-200 text-gray-900 whitespace-pre-wrap">
                          {equipment.spareParts}
                        </td>
                      </tr>
                    )}
                    {equipment.firstAidKit && (
                      <tr>
                        <td className="px-4 py-3 border border-gray-200 bg-gray-50 font-medium text-gray-700">
                          Boîte à pharmacie
                        </td>
                        <td className="px-4 py-3 border border-gray-200 text-gray-900 whitespace-pre-wrap">
                          {equipment.firstAidKit}
                        </td>
                      </tr>
                    )}
                    {equipment.notes && (
                      <tr>
                        <td className="px-4 py-3 border border-gray-200 bg-gray-50 font-medium text-gray-700">
                          Notes
                        </td>
                        <td className="px-4 py-3 border border-gray-200 text-gray-900 whitespace-pre-wrap">
                          {equipment.notes}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <Card>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Dates importantes</h2>
            <div className="space-y-4">
              {equipment.manufacturingDate && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-700">Date de fabrication</span>
                  </div>
                  <span className="font-medium text-gray-900">
                    {formatDate(equipment.manufacturingDate)}
                  </span>
                </div>
              )}
              {equipment.lastMaintenanceDate && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <span className="text-gray-700">Date de contrôle</span>
                  </div>
                  <span className="font-medium text-gray-900">
                    {formatDate(equipment.lastMaintenanceDate)}
                  </span>
                </div>
              )}
              {equipment.nextMaintenanceDate && (
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    <span className="text-blue-700 font-medium">Prochaine vérification</span>
                  </div>
                  <span className="font-medium text-blue-900">
                    {formatDate(equipment.nextMaintenanceDate)}
                  </span>
                </div>
              )}
              {equipment.expirationDate && (
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Calendar className="w-5 h-5 text-orange-500" />
                    <span className="text-orange-700 font-medium">Date de recharge</span>
                  </div>
                  <span className="font-medium text-orange-900">
                    {formatDate(equipment.expirationDate)}
                  </span>
                </div>
              )}
            </div>
          </Card>

        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Actions rapides</h3>
            <div className="space-y-3">
              <Button variant="primary" className="w-full" icon={MapPin} onClick={() => navigate('/my-plan')}>
                Voir sur le plan
              </Button>
              <Button variant="outline" className="w-full" icon={FileText} onClick={() => navigate('/my-reports')}>
                Télécharger la fiche
              </Button>
            </div>
          </Card>

          <Card>
            <h3 className="font-semibold text-gray-900 mb-4">Statut</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">État actuel</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                  {getStatusLabel(status)}
                </span>
              </div>
              {daysUntilMaintenance !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Maintenance dans</span>
                  <span className="text-sm font-medium text-gray-900">
                    {daysUntilMaintenance} jour(s)
                  </span>
                </div>
              )}
              {daysUntilExpiration !== null && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Expire dans</span>
                  <span className="text-sm font-medium text-gray-900">
                    {daysUntilExpiration} jour(s)
                  </span>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EquipmentDetails;

